import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';

import { compressImage } from '@/utils/imageCompression';

export default function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [listingTypes, setListingTypes] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/categories'),
      api.get('/listing-types'),
      api.get(`/my/listings`),
    ]).then(([cats, types, myListings]) => {
      setCategories(cats.data);
      setListingTypes(types.data);
      const listing = myListings.data.find(l => l.id === id);
      if (listing) {
        setForm({ title: listing.title || '', description: listing.description || '', category_id: listing.category_id || '', type_id: listing.type_id || '', price: listing.price || '', currency: listing.currency || 'GBP', year: listing.year || '', dimensions: listing.dimensions || '', medium: listing.medium || '', condition: listing.condition || '', provenance: listing.provenance || '', status: listing.status || 'draft', images: listing.images || [] });
      } else {
        toast.error('Listing not found'); navigate('/dashboard/listings');
      }
    }).catch(() => { toast.error('Failed to load listing'); navigate('/dashboard/listings'); }).finally(() => setLoading(false));
  }, [id]);

  const handleImageAdd = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      setUploading(true);
      try {
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append('file', compressed);
        const r = await api.post(`/listings/${id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(f => ({ ...f, images: [...f.images, r.data.image_url] }));
      } catch (e) { toast.error('Failed to upload image'); } finally { setUploading(false); }
    }
    e.target.value = '';
  };

  const removeImage = async (imgUrl) => {
    try {
      await api.delete(`/listings/${id}/images?image_url=${encodeURIComponent(imgUrl)}`);
      setForm(f => ({ ...f, images: f.images.filter(u => u !== imgUrl) }));
    } catch (e) { toast.error('Failed to remove image'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : null };
      delete payload.images;
      await api.put(`/my/listings/${id}`, payload);
      toast.success('Listing updated!');
      navigate('/dashboard/listings');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update listing');
    } finally { setSaving(false); }
  };

  if (loading || !form) return <DashboardShell title="Edit Listing"><div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1C1C1C] border-t-transparent" /></div></DashboardShell>;

  return (
    <DashboardShell title="Edit Listing">
      <form className="max-w-2xl space-y-6" onSubmit={handleSubmit}>
        {/* Images */}
        <div>
          <Label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>Images</Label>
          <div className="flex flex-wrap gap-3">
            {form.images.map((url, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-clr)' }}>
                <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = PLACEHOLDER_IMAGE; }} />
                <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center"><X size={10} style={{ color: 'white' }} /></button>
              </div>
            ))}
            <label className="w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer" style={{ borderColor: 'var(--border-clr)' }}>
              {uploading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#1C1C1C] border-t-transparent" /> : <><Upload size={16} style={{ color: 'var(--text-muted)' }} /><span className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Add</span></>}
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageAdd} />
            </label>
          </div>
        </div>

        <div className="space-y-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)' }}>
          <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} required style={{ borderColor: 'var(--border-clr)' }} /></div>
          <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} style={{ borderColor: 'var(--border-clr)' }} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Category *</Label><Select value={form.category_id} onValueChange={v => setForm(f => ({...f, category_id: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Listing Type *</Label><Select value={form.type_id} onValueChange={v => setForm(f => ({...f, type_id: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{listingTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </div>

        <div className="space-y-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)' }}>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2"><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Price</Label><Input type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Currency</Label><Select value={form.currency} onValueChange={v => setForm(f => ({...f, currency: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['GBP','EUR','USD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Year</Label><Input value={form.year} onChange={e => setForm(f => ({...f, year: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Dimensions</Label><Input value={form.dimensions} onChange={e => setForm(f => ({...f, dimensions: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Medium</Label><Input value={form.medium} onChange={e => setForm(f => ({...f, medium: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} /></div>
            <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Condition</Label><Input value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))} style={{ borderColor: 'var(--border-clr)' }} /></div>
          </div>
          <div><Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Status</Label><Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['draft','active','sold'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/dashboard/listings')} style={{ borderColor: 'var(--border-strong)' }}>Cancel</Button>
          <Button type="submit" disabled={saving} style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '10px' }}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </form>
    </DashboardShell>
  );
}
